import { t } from '@lingui/core/macro';
import React from 'react';
import { FileText, Folder, Library, Network, Plus, RefreshCw, User as UserIcon } from 'lucide-react';
import { lucideByName } from '@src/lib/lucide-by-name';
import { documentsTreeType, isDocumentFilePath, isMarkdownDocumentPath } from '@src/lib/markdown-path';
import { iconForType } from '@src/components/graph-view/icons/iconRegistry';
import apiClient from '@sdk/client';
import { RecordType, TypeId } from '@sdk';
import { subscribeToEntityOps } from '@sdk/FlowSync/entity-ops';
import { RagFolderIcon } from '@src/components/browseable-tree/RagFolderIcon';
import { RagToggleGlyph } from '@src/components/rag/RagToggleGlyph';
import { toggleRoot } from '@src/components/rag/rag-service';
import { DockPointer } from '@src/navigation/DockPointer';
import { ViewType } from '@src/types/ViewType';
import type { AssetTypeInfo, AssetTypeVault } from '@src/hooks/use-asset-types';
import type {
  Browseable,
  BrowseableDragData,
  BrowseableRoot,
  ToolbarAction,
} from '@src/components/browseable-tree/types';
import { AssetTypeCountBadge, parseAssetPointer } from './assetTypeRoot';
import { onFsFoldersChanged, withFsOps, type FsOpsCtx, type FsOpsHost } from './fsFileOps';
import { refreshNode } from '@src/components/browseable-tree/refresh-store';
import { isAllScope, scopeIncludesUser, scopeProjectIds, type ScopeFilter } from '@src/lib/scope-filter';
import { DEFAULT_ASSET_FILTER } from '@src/components/assets/assetFilter';
import type { AssetFilter } from '@src/components/assets/assetFilter';

export interface MarkdownFolderRootDeps {
  /** Reindex callback for the "Scan" toolbar button. Forwards the active
   *  filter so per-type scans honor the same scope. */
  indexType: (
    typeName: string,
    scope?: ScopeFilter,
    options?: { force?: boolean; orphanAction?: 'index' | 'ignore' | 'delete' },
  ) => Promise<{ indexed?: number } | void>;
  /** Called when the root-level "New" toolbar is clicked (falls back to the
   *  legacy flow which created under .claude/docs). */
  onNew?: (typeName: string) => void;
  /** Active filter — used by the count badge so it tracks scope/project. */
  filter?: AssetFilter;
  /** Open the docs knowledge browser for a vault root (its absolute vfs path).
   *  Wired by the host (AssetsPage) to navigation.openDock — toolbar actions are
   *  side-effects, so navigation is performed by the host, not here. */
  onOpenKnowledgeBrowser?: (absPath: string) => void;
  /** When set, every vault, folder and file row gets the file-manager
   *  actions (toolbar, right-click, keys, drag move/copy, upload) — see
   *  `fsFileOps`. */
  fileOps?: FsOpsHost;
}

export interface MarkdownDragItem extends BrowseableDragData {
  kind: 'markdown-file' | 'markdown-folder';
  typeName: string;
  typeid: string;
  relPath: string;
  absPath: string;
  isDir: boolean;
}

export function markdownFolderNodeId(typeid: string, absPath: string): string {
  return `md-folder:${typeid}:${absPath || '/'}`;
}

function normalizeRelPath(path: string): string {
  return path.replace(/^\/+/, '').replace(/\/+$/, '');
}


function resolveAssetIcon(iconName: string | null | undefined): React.ReactNode {
  const Icon = lucideByName(iconName);
  return <Icon className="h-4 w-4 flex-shrink-0" />;
}

/** Build the root-level toolbar: Scan + New. */
function rootToolbar(type: AssetTypeInfo, deps: MarkdownFolderRootDeps): ToolbarAction[] {
  const actions: ToolbarAction[] = [
    {
      id: `scan:${type.type_name}`,
      icon: <RefreshCw />,
      label: t`Scan for changes`,
      run: async () => {
        // Reindex; resulting data_ops refresh the tree via useAssetTreeRefresh.
        await deps.indexType(type.type_name, deps.filter?.scope);
      },
    },
  ];
  if (deps.onNew) {
    actions.push({
      id: `new:${type.type_name}`,
      icon: <Plus />,
      label: t`New ${type.label}`,
      run: () => deps.onNew?.(type.type_name),
      showBusyIndicator: false,
    });
  }
  return actions;
}

/**
 * Fetch the COMPLETE set of markdown files under a vault root, honoring
 * ``.gitignore`` (backend ``/assets/markdown-files`` → ``walk_markdown_files``).
 * Returns vault-root-relative POSIX paths. Memoised per absolute root so the
 * vault node's ``listChildren`` and ``pathFor`` (deep-link) share one request.
 */
const _vaultFilesCache = new Map<string, Promise<string[]>>();

// The walk outlives any one tree (a remounted tree starts with no state but this
// module keeps its cache), so a create or delete of a listed file drops it here,
// once — otherwise the next expand serves a walk from before the change.
let _walksWatched = false;
function forgetWalksOnFileChanges(): void {
  if (_walksWatched) return;
  _walksWatched = true;
  subscribeToEntityOps([RecordType.MARKDOWN, RecordType.DOCUMENT_FILE], () => _vaultFilesCache.clear(), {
    ops: ['create', 'delete'],
  });
  // A change made from another tree (Files) under a vault: re-walk that vault.
  onFsFoldersChanged((paths) => {
    for (const [vaultAbs, nodeId] of _vaultNodeIds) {
      if (paths.some((p) => p === vaultAbs || p.startsWith(`${vaultAbs}/`))) {
        _vaultFilesCache.delete(vaultAbs);
        refreshNode(nodeId);
      }
    }
  });
}

/** Vault root path → its tree node id, for the cross-tree refresh above. */
const _vaultNodeIds = new Map<string, string>();

/** Test-only: drop the per-vault walk memoisation so each case fetches fresh. */
export function __resetVaultFilesCacheForTests(): void {
  _vaultFilesCache.clear();
  _walksWatched = false;
}

function fetchVaultFiles(vaultAbsPath: string, refresh = false): Promise<string[]> {
  forgetWalksOnFileChanges();
  if (refresh) _vaultFilesCache.delete(vaultAbsPath);
  const cached = _vaultFilesCache.get(vaultAbsPath);
  if (cached) return cached;
  const params = new URLSearchParams({ root: vaultAbsPath });
  const p = apiClient
    .get(`/assets/markdown-files?${params.toString()}`)
    .then((d: unknown) => (d as { files?: string[] } | null)?.files ?? [])
    .catch(() => [] as string[]);
  _vaultFilesCache.set(vaultAbsPath, p);
  return p;
}

/** Absolute path of ``prefixRel`` (vault-relative) under a vault root. */
function vaultAbsForPrefix(vaultAbsPath: string, prefixRel: string): string {
  const p = normalizeRelPath(prefixRel);
  return p ? `${vaultAbsPath.replace(/\/+$/, '')}/${p}` : vaultAbsPath;
}

/** VFS rel path of ``prefixRel`` under a vault (the DockPointer folder form). */
function vaultRelForPrefix(vaultRelPath: string, prefixRel: string): string {
  const p = normalizeRelPath(prefixRel);
  if (!p) return vaultRelPath;
  return vaultRelPath ? `${vaultRelPath}/${p}` : p;
}

/**
 * Immediate children (subfolders + ``.md`` files) directly under ``prefixRel``
 * within a vault, derived from the flat walk ``files``. Folders sort before
 * files; both alphabetical. Subfolder nodes recurse over the SAME ``files``
 * array (no extra fetch); the whole subtree comes from the one walk.
 */
export function childrenForPrefix(args: {
  typeName: string;
  typeid: string;
  vaultAbsPath: string;
  vaultRelPath: string;
  files: string[];
  prefixRel: string; // vault-relative dir, '' for the vault root
  ops?: FsOpsCtx;
}): Browseable[] {
  const { typeName, typeid, vaultAbsPath, vaultRelPath, files, prefixRel, ops } = args;
  const norm = normalizeRelPath(prefixRel);
  const head = norm ? `${norm}/` : '';
  const folderNames = new Set<string>();
  const fileNames: string[] = [];
  for (const f of files) {
    if (head && !f.startsWith(head)) continue;
    const remainder = head ? f.slice(head.length) : f;
    if (!remainder) continue;
    const slash = remainder.indexOf('/');
    if (slash === -1) {
      fileNames.push(remainder);
    } else {
      folderNames.add(remainder.slice(0, slash));
    }
  }

  const folders: Browseable[] = [...folderNames]
    .sort((a, b) => a.localeCompare(b))
    .map((name) =>
      folderBrowseable({
        typeName,
        typeid,
        vaultAbsPath,
        vaultRelPath,
        files,
        prefixRel: norm ? `${norm}/${name}` : name,
        label: name,
        kind: 'folder',
        ops,
      }),
    );

  const fileLeaves: Browseable[] = fileNames
    .sort((a, b) => a.localeCompare(b))
    .map((name) => {
      const childRel = norm ? `${norm}/${name}` : name;
      const childAbs = vaultAbsForPrefix(vaultAbsPath, childRel);
      const leafType = documentsTreeType(name);
      const LeafIcon = iconForType(leafType);
      const leaf: Browseable = {
        id: `md-file:${typeid}:${childAbs}`,
        kind: 'asset',
        label: name,
        icon: <LeafIcon className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />,
        hasChildren: false as const,
        pointer: DockPointer.forAssetEditor(leafType, childAbs),
        dragData: {
          kind: 'markdown-file',
          id: `md-file:${typeid}:${childAbs}`,
          label: name,
          typeName,
          typeid,
          relPath: normalizeRelPath(vaultRelForPrefix(vaultRelPath, childRel)),
          absPath: childAbs,
          isDir: false,
        },
      };
      const rel = normalizeRelPath(vaultRelForPrefix(vaultRelPath, childRel));
      return ops ? withFsOps(leaf, ops, { rel, isDir: false, label: name }) : leaf;
    });

  return [...folders, ...fileLeaves];
}

/**
 * Build a Browseable for a folder (or the vault root) backed by the flat
 * gitignore-aware walk. Children are derived in-memory from ``files`` — no
 * per-folder filesystem listing — so a project-root file appears next to
 * ``docs/`` files. The vault root fetches the walk; folders reuse it.
 */
function folderBrowseable(args: {
  typeName: string;
  typeid: string;
  vaultAbsPath: string;
  vaultRelPath: string;
  prefixRel: string; // vault-relative dir, '' for the vault root
  label: string;
  kind: 'vault-root' | 'folder';
  files?: string[]; // present for descendants; the vault root fetches it
  onOpenKnowledgeBrowser?: (absPath: string) => void;
  ops?: FsOpsCtx;
}): Browseable {
  const {
    typeName,
    typeid,
    vaultAbsPath,
    vaultRelPath,
    prefixRel,
    label,
    kind,
    files,
    onOpenKnowledgeBrowser,
    ops,
  } = args;
  // This folder's own paths derive from the vault root + its vault-relative prefix.
  const absPath = vaultAbsForPrefix(vaultAbsPath, prefixRel);
  const relPath = vaultRelForPrefix(vaultRelPath, prefixRel);
  // The knowledge-browser button sits only on the docs root (vault-root), which
  // has a single scannable path; subfolders don't get their own browser.
  const kbAction: ToolbarAction[] =
    kind === 'vault-root' && onOpenKnowledgeBrowser
      ? [
          {
            id: `kbrowser:${typeid}:${absPath}`,
            icon: <Network />,
            label: t`Open knowledge browser`,
            run: () => onOpenKnowledgeBrowser(absPath),
            showBusyIndicator: false,
          },
        ]
      : [];
  // Every docs folder can be made searchable, so every row carries the toggle — beside the
  // knowledge browser, which is the same kind of thing: an action ON this folder rather than a
  // way into it. The backend finds or creates the box's index, so the first folder anybody
  // marks needs no visit to the Search indexes screen first.
  const ragAction: ToolbarAction = {
    id: `rag:${typeid}:${absPath}`,
    icon: <RagToggleGlyph path={absPath} />,
    // Neutral wording on purpose: the label is fixed when the row is BUILT, while the answer
    // it describes changes at render time. "Index this folder" on a button that is about to
    // UN-index it is worse than naming what the button is for.
    label: t`Search indexing for this folder`,
    // The wrapper is load-bearing: `toggleRoot` resolves to the new coverage, and
    // `ToolbarAction.run` is `void | Promise<void>` — returning it directly is a type error.
    run: async () => {
      await toggleRoot(absPath);
    },
  };
  const node: Browseable = {
    id: markdownFolderNodeId(typeid, absPath),
    kind,
    label,
    // A docs folder is as indexable as any other, so it wears the same marker; `absPath` is
    // already the machine path the roots are stored as.
    icon: <RagFolderIcon Base={Folder} path={absPath} size={kind === 'vault-root' ? 'h-4 w-4' : 'h-3.5 w-3.5'} />,
    hasChildren: true,
    pointer: DockPointer.forAssetFolder(typeName, typeid, relPath),
    toolbar: [ragAction, ...kbAction],
    dragData:
      kind === 'folder'
        ? {
            kind: 'markdown-folder',
            id: `md-folder:${typeid}:${absPath}`,
            label,
            typeName,
            typeid,
            relPath: normalizeRelPath(relPath),
            absPath,
            isDir: true,
          }
        : undefined,
    listChildren: async (opts) => {
      // The vault root owns the walk; `refresh` re-walks (deep-link freshness
      // when a leaf was missing from a cached listing). Descendants reuse the
      // already-fetched `files` array — no extra request.
      const walk = files ?? (await fetchVaultFiles(vaultAbsPath, !!opts?.refresh));
      return childrenForPrefix({
        typeName,
        typeid,
        vaultAbsPath,
        vaultRelPath,
        files: walk,
        prefixRel,
        ops,
      });
    },
  };
  return ops
    ? withFsOps(node, ops, { rel: normalizeRelPath(relPath), isDir: true, label }, { isTop: kind === 'vault-root' })
    : node;
}

/** Mirror of backend ``apply_scope_filter`` for vault listing. Reads the
 *  unified ScopeFilter `{user, projects}`: a user vault is kept iff
 *  `sf.user`; a project vault is kept iff its Project id or legacy
 *  record_project_id is selected. Empty `projects` means no project vaults —
 *  except in the `all` scope, which selects no SPECIFIC project yet means
 *  every vault. */
function keepVault(v: AssetTypeVault, filter: AssetFilter): boolean {
  if (isAllScope(filter.scope)) return true;
  if (v.scope === 'user') return scopeIncludesUser(filter.scope);
  if (v.scope === 'project') {
    const selected = new Set(scopeProjectIds(filter.scope));
    return (
      (!!v.project_id && selected.has(v.project_id)) || (!!v.record_project_id && selected.has(v.record_project_id))
    );
  }
  return false;
}

/**
 * The resource path this root can show, or null. A vault lists folders and
 * markdown files only, so any other file under a vault (an `.html` beside the
 * docs) is not this root's to claim: claiming it built a leaf the listing can
 * never contain, and the tree's deep-link freshness check then re-fetched the
 * vault — flashing it to "Loading…" — on every re-render (2026-10-02).
 */
function markdownResourcePath(p: DockPointer): string | null {
  const resourcePath = p.resourceVfsPath?.machinePath ?? null;
  return isMarkdownDocumentPath(resourcePath) || isDocumentFilePath(resourcePath) ? resourcePath : null;
}

function findVaultForAbsPath(vaults: AssetTypeVault[], absPath: string): AssetTypeVault | null {
  // Pick the most specific (longest absPath) vault that is a prefix of `absPath`.
  let best: AssetTypeVault | null = null;
  for (const v of vaults) {
    if (absPath === v.absPath || absPath.startsWith(v.absPath + '/')) {
      if (!best || v.absPath.length > best.absPath.length) best = v;
    }
  }
  return best;
}

function findVaultForTypeidRel(vaults: AssetTypeVault[], typeid: string, relPath: string): AssetTypeVault | null {
  let best: AssetTypeVault | null = null;
  for (const v of vaults) {
    if (v.typeid !== typeid) continue;
    if (relPath === v.relPath || relPath.startsWith(v.relPath + (v.relPath ? '/' : ''))) {
      if (!best || v.relPath.length > best.relPath.length) best = v;
    }
  }
  return best;
}

/**
 * Build the Markdown root for the Obsidian-style folder tree. Children are
 * vault roots (from AssetTypeInfo.vaults); each vault's subtree comes from one
 * gitignore-aware project walk (``/assets/markdown-files``), built in memory.
 */
export function markdownFolderRoot(type: AssetTypeInfo, deps: MarkdownFolderRootDeps): BrowseableRoot {
  const vaults = type.vaults ?? [];
  const filter = deps.filter ?? DEFAULT_ASSET_FILTER;

  // A vault keeps the glyph that says which vault it is, and wears the brain on top of it when
  // it is a RAG root — the two facts are independent, so one must not replace the other.
  const vaultIcon = (v: AssetTypeVault): React.ReactNode => {
    const Base = v.scope === 'user' ? UserIcon : v.scope === 'project' ? Library : Folder;
    return <RagFolderIcon Base={Base} path={v.absPath} size="h-4 w-4" />;
  };

  // One file-ops binding per vault: its paths live on the vault's entity, and
  // any change re-walks the vault (its sub-folders list from that one walk).
  const opsByVault = new Map<string, FsOpsCtx>();
  const vaultOps = (v: AssetTypeVault): FsOpsCtx | undefined => {
    if (!deps.fileOps) return undefined;
    let ctx = opsByVault.get(v.absPath);
    if (!ctx) {
      const vaultRel = normalizeRelPath(v.relPath);
      const inVault = (rel: string) => {
        const r = normalizeRelPath(rel);
        return vaultRel && r.startsWith(`${vaultRel}/`) ? r.slice(vaultRel.length + 1) : r === vaultRel ? '' : r;
      };
      const machinePathFor = (rel: string) => vaultAbsForPrefix(v.absPath, inVault(rel));
      const vaultNodeId = markdownFolderNodeId(v.typeid, v.absPath);
      _vaultNodeIds.set(v.absPath, vaultNodeId);
      const typeId = new TypeId(v.typeid);
      ctx = {
        typeId,
        locatorTypeId: typeId,
        anchor: vaultRel,
        dirNodeId: () => vaultNodeId,
        machinePathFor,
        relForMachinePath: (machine) => {
          const vaultAbs = v.absPath.replace(/\/+$/, '');
          if (machine !== vaultAbs && !machine.startsWith(`${vaultAbs}/`)) return null;
          const suffix = normalizeRelPath(machine.slice(vaultAbs.length));
          return suffix ? (vaultRel ? `${vaultRel}/${suffix}` : suffix) : vaultRel;
        },
        filePointerFor: (path) => {
          const machine = machinePathFor(path.entitySubPath);
          return DockPointer.forAssetEditor(documentsTreeType(machine), machine);
        },
        // The re-walk itself comes from the change signal (forgetWalksOnFileChanges);
        // here only keep search and counts in step with the disk, without waiting on it.
        afterChange: () => {
          deps.indexType(type.type_name, deps.filter?.scope, { force: true }).catch((err) => {
            console.error('[markdownFolderRoot] reindex after file change failed:', err);
          });
        },
        host: deps.fileOps,
      };
      opsByVault.set(v.absPath, ctx);
    }
    return ctx;
  };

  const buildVaultNode = (v: AssetTypeVault): Browseable => ({
    ...folderBrowseable({
      typeName: type.type_name,
      typeid: v.typeid,
      vaultAbsPath: v.absPath,
      vaultRelPath: v.relPath,
      prefixRel: '',
      label: v.label,
      kind: 'vault-root',
      onOpenKnowledgeBrowser: deps.onOpenKnowledgeBrowser,
      ops: vaultOps(v),
    }),
    icon: vaultIcon(v),
  });

  const visibleVaults = vaults.filter((v) => keepVault(v, filter));
  // STABLE id, like assetTypeRoot's: a scope or vault-list change must not
  // remount the root (dropping its children — the sidebar blink). Those changes
  // re-list it through an invalidate instead: the scope-change effect in
  // useAssetTreeRefresh, and the vault-list effect in useAssetsModel.

  const root: BrowseableRoot = {
    id: `asset-type:${type.type_name}`,
    kind: 'root',
    label: type.label,
    icon: resolveAssetIcon(type.icon),
    // The SAME badge every other type root uses, so markdown reports the same
    // thing they do — a count accumulated over the project AND its context
    // folders. Its old private `/search?limit=1` probe was scoped to the project
    // alone, which left this row silently under-reporting next to the others
    // (and cost one extra request per mount).
    badge: <AssetTypeCountBadge typeName={type.type_name} />,
    hasChildren: visibleVaults.length > 0,
    pointer: DockPointer.forAssetList(type.type_name),
    toolbar: rootToolbar(type, deps),
    listChildren: () => Promise.resolve(visibleVaults.map(buildVaultNode)),
    ownsPointer: (p) => {
      // A canonical VFS resource is owned independently of the route used to
      // present it (editor, Assets Files, Explorer).
      const resourcePath = markdownResourcePath(p);
      if (resourcePath && findVaultForAbsPath(visibleVaults, resourcePath)) return true;
      if (p.viewType !== ViewType.ASSETS) return false;
      // Non-resource routes retain their semantic ownership.
      const flat = parseAssetPointer(p.pointer ?? null);
      if (flat.typeName === type.type_name) return true;
      const folder = DockPointer.parseAssetFolderPointer(p.pointer);
      return folder !== null && folder.typeName === type.type_name;
    },
    pathFor: (p) => {
      // Folder pointer → walk vault + descendant folders
      const folder = DockPointer.parseAssetFolderPointer(p.pointer);
      if (folder) {
        const vault = findVaultForTypeidRel(vaults, folder.typeid, folder.relPath);
        if (!vault) return Promise.resolve([root]);
        const chain: Browseable[] = [root, buildVaultNode(vault)];
        if (folder.relPath === vault.relPath) return Promise.resolve(chain);
        const extra = folder.relPath.slice(vault.relPath.length).replace(/^\/+/, '').replace(/\/+$/, '');
        if (!extra) return Promise.resolve(chain);
        let currentPrefix = '';
        for (const seg of extra.split('/')) {
          currentPrefix = currentPrefix ? `${currentPrefix}/${seg}` : seg;
          chain.push(
            folderBrowseable({
              typeName: type.type_name,
              typeid: vault.typeid,
              vaultAbsPath: vault.absPath,
              vaultRelPath: vault.relPath,
              prefixRel: currentPrefix,
              label: seg,
              kind: 'folder',
              ops: vaultOps(vault),
            }),
          );
        }
        return Promise.resolve(chain);
      }

      // VFS resource → walk vault + intermediate folders + leaf file,
      // regardless of whether the active route is editor, fs, or Explorer.
      const absPath = markdownResourcePath(p);
      if (absPath) {
        const vault = findVaultForAbsPath(vaults, absPath);
        if (!vault) return Promise.resolve([root]);
        const chain: Browseable[] = [root, buildVaultNode(vault)];
        const remainder = absPath.slice(vault.absPath.length).replace(/^\/+/, '').replace(/\/+$/, '');
        if (!remainder) return Promise.resolve(chain);
        const segments = remainder.split('/');
        const fileName = segments.pop()!;
        let currentPrefix = '';
        for (const seg of segments) {
          currentPrefix = currentPrefix ? `${currentPrefix}/${seg}` : seg;
          chain.push(
            folderBrowseable({
              typeName: type.type_name,
              typeid: vault.typeid,
              vaultAbsPath: vault.absPath,
              vaultRelPath: vault.relPath,
              prefixRel: currentPrefix,
              label: seg,
              kind: 'folder',
              ops: vaultOps(vault),
            }),
          );
        }
        chain.push({
          id: `md-file:${vault.typeid}:${absPath}`,
          kind: 'asset',
          label: fileName,
          icon: <FileText className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />,
          hasChildren: false,
          pointer: DockPointer.forAssetEditor(type.type_name, absPath),
        });
        return Promise.resolve(chain);
      }

      return Promise.resolve([root]);
    },
  };
  return root;
}
